import React from 'react';
import type { DriveItem } from '../types';
import { driveTypeLabel, formatDate, formatSize } from '../lib/drive-items';
import { DriveItemIcon, DriveItemMenu, SelectAllCheckbox, type DriveItemActions } from './DriveItemParts';

interface DriveListProps {
  items: DriveItem[];
  selectedItems: string[];
  onSelect: (itemId: string, isSelected: boolean) => void;
  onSelectAll: (all: boolean) => void;
  actions: DriveItemActions;
}

// Row: checkbox 1rem | icon 1rem | name (flex, min 12rem) | type 6rem | size 6rem | date 8rem | menu 3.5rem
export function DriveList({ items, selectedItems, onSelect, onSelectAll, actions }: DriveListProps) {
  const selected = new Set(selectedItems);
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <div style={{ minWidth: '47.25rem' }}>
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center">
            <div className="w-4 flex-shrink-0">
              <SelectAllCheckbox total={items.length} selected={items.filter((i) => selected.has(i.id)).length} onChange={onSelectAll} />
            </div>
            <div className="w-4 flex-shrink-0 ml-3" />
            <div className="flex-1 px-3 font-table-head" style={{ minWidth: '12rem' }}>Name</div>
            <div className="flex-shrink-0 px-3 font-table-head w-24">Type</div>
            <div className="flex-shrink-0 px-3 font-table-head w-24">Size</div>
            <div className="flex-shrink-0 px-3 font-table-head w-32">Modified</div>
            <div className="flex-shrink-0 w-14" />
          </div>
          <div className="divide-y divide-gray-100">
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex items-center px-3 py-1 hover:bg-gray-50 transition-colors cursor-pointer ${selected.has(item.id) ? 'bg-blue-50' : ''}`}
                onClick={() => actions.onOpen(item)}
              >
                <div className="w-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${item.name}`}
                    checked={selected.has(item.id)}
                    onChange={(e) => onSelect(item.id, e.target.checked)}
                    className="h-3.5 w-3.5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                  />
                </div>
                <div className="w-4 flex-shrink-0 ml-3"><DriveItemIcon item={item} /></div>
                <div className="flex-1 px-3 overflow-hidden" style={{ minWidth: '12rem' }}>
                  <span className="font-table-body truncate block hover:text-blue-600" title={item.type === 'url' ? item.url : item.name}>{item.name}{item.system_key ? <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">{item.system_key === 'shared_with_client' ? 'Visible to the client' : 'Visible to vendors'}</span> : null}</span>
                </div>
                <div className="flex-shrink-0 px-3 w-24 font-table-body-muted truncate">{driveTypeLabel(item)}</div>
                <div className="flex-shrink-0 px-3 w-24 font-table-body-muted truncate">{item.type === 'file' ? formatSize(item.size) : ''}</div>
                <div className="flex-shrink-0 px-3 w-32 font-table-body-muted truncate">{formatDate(item.updated_at)}</div>
                <div className="flex-shrink-0 w-14 flex justify-end pr-3">
                  <DriveItemMenu item={item} actions={actions} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
